import { motion } from "framer-motion";
import BrandName from "@/components/site/BrandName";
import PremiumCertificate from "@/components/site/PremiumCertificate";
import ScaledCertificate from "@/components/certificate/ScaledCertificate";

/**
 * Three sample certificates spanning different regulated competencies.
 * Illustrates that CanniComply governs competency across the full
 * compliance surface, not just one assessment.
 */
const samples = [
  {
    id: "back-left",
    style: {
      transform: "rotate(-5.5deg) translate(-24%, 3%) scale(0.94)",
      zIndex: 10,
      opacity: 0.9,
    },
    props: {
      competencyTitle: "Medical Cannabis Dispensing",
      staffName: "James Patel",
      staffRole: "Registered Pharmacist",
      organisation: "Northgate Pharmacy Ltd.",
      sopReferences: ["SOP-DP-04 v3.1"],
      score: "94%",
      passMark: "80%",
      completedOn: "9 February 2026",
      validUntil: "9 February 2028",
      certId: "CC-2026-000114",
      version: "1.0",
    },
  },
  {
    id: "back-right",
    style: {
      transform: "rotate(5.5deg) translate(24%, 3%) scale(0.94)",
      zIndex: 10,
      opacity: 0.9,
    },
    props: {
      competencyTitle: "GDP Compliance",
      staffName: "Emma Williams",
      staffRole: "Quality Assurance Manager",
      organisation: "Meridian Cultivation Ltd.",
      sopReferences: ["SOP-GDP-11 v2.4", "SOP-GDP-12 v2.4"],
      score: "97%",
      passMark: "85%",
      completedOn: "11 February 2026",
      validUntil: "11 February 2027",
      certId: "CC-2026-000132",
      version: "1.0",
    },
  },
  {
    id: "front",
    style: { zIndex: 20 },
    props: {
      competencyTitle: "Introduction to Medical Cannabis Compliance",
      staffName: "Jane Smith",
      staffRole: "",
      organisation: "",
      sopReferences: [],
      score: "\u2014",
      passMark: "80%",
      completedOn: "23 July 2026",
      validUntil: "23 July 2027",
      certId: "CC-2026-001247",
      version: "1.0",
    },
  },
];

export default function CertificateMock() {
  return (
    <section
      id="certificate"
      data-testid="certificate-section"
      className="relative py-14 md:py-24 border-t border-white/[0.04]"
    >
      {/* grid-cols-1 explicitly forces the mobile/tablet stack to a
          single full-width column. Without it, `grid` with no
          grid-template-columns lets the browser place items into
          implicit auto-sized columns that shrink to content, which
          collapsed the certificate preview to a tiny thumbnail on
          tablet widths. */}
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        {/* Certificate visual — shown on every viewport now. Two variants:
              • Mobile / tablet  → a single Jane Smith cert rendered via
                ScaledCertificate (bulletproof transform-scale approach).
              • Desktop (≥ lg)   → the original fanned 3-card stack. */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 order-2 lg:order-1"
        >
          {/* ── Mobile / tablet variant: single scaled cert ──
              Uses an explicit flex-centered outer wrapper (w-full) so the
              inner column resolves to a real width before ScaledCertificate
              measures it. On grid items, `mx-auto max-w-[N]` shrinks the
              wrapper to content width — which starves ScaledCertificate's
              ResizeObserver of a parent width and collapses the preview to
              a tiny thumbnail. Flex + explicit w-full on the inner column
              guarantees a real inline size on every breakpoint. */}
          <div
            className="lg:hidden w-full flex justify-center"
            data-testid="certificate-stack-mobile"
          >
            <div className="relative w-full max-w-[720px]">
              <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-gradient-to-br from-[#D4A65A]/[0.10] via-transparent to-emerald-500/[0.05] blur-3xl" />
              <div className="relative w-full">
                <ScaledCertificate {...samples[2].props} />
              </div>
              <div className="mt-5 text-center text-[10.5px] uppercase tracking-[0.22em] text-slate-500">
                {"A4 landscape \u00b7 print-ready"}
              </div>
            </div>
          </div>

          {/* ── Desktop variant: fanned 3-card stack (unchanged) ── */}
          <div
            className="relative mx-auto max-w-[520px] hidden lg:block"
            data-testid="certificate-stack"
          >
            <style>{`
              .cert-back-left  { transform: rotate(-6deg) translate(-22%, 4%) scale(0.94); transform-origin: center 45%; }
              .cert-back-right { transform: rotate( 6deg) translate( 22%, 4%) scale(0.94); transform-origin: center 45%; }
              @media (max-width: 640px) {
                .cert-back-left,
                .cert-back-right { display: none; }
              }
            `}</style>
            {/* Ambient warm glow behind the stack */}
            <div className="pointer-events-none absolute -inset-8 rounded-3xl bg-gradient-to-br from-[#D4A65A]/[0.10] via-transparent to-emerald-500/[0.05] blur-3xl" />

            <div className="relative">
              {/* Back-left — absolutely positioned so it never adds to height.
                  The CSS transform (rotate/translate/scale) lives on an INNER
                  div so it doesn't get overwritten by framer-motion's own
                  transform on the outer motion.div. */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="absolute inset-0"
                style={{ zIndex: 10 }}
                aria-hidden="true"
              >
                <div
                  className="cert-back-left w-full h-full"
                  style={{ filter: "brightness(0.92) contrast(0.98)" }}
                >
                  <ScaledCertificate {...samples[0].props} />
                </div>
              </motion.div>

              {/* Back-right */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="absolute inset-0"
                style={{ zIndex: 10 }}
                aria-hidden="true"
              >
                <div
                  className="cert-back-right w-full h-full"
                  style={{ filter: "brightness(0.92) contrast(0.98)" }}
                >
                  <ScaledCertificate {...samples[1].props} />
                </div>
              </motion.div>

              {/* FRONT — in-flow, defines the wrapper's natural height */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="relative"
                style={{ zIndex: 20 }}
              >
                <ScaledCertificate {...samples[2].props} />
              </motion.div>
            </div>

            <div className="mt-6 text-center text-[10.5px] uppercase tracking-[0.22em] text-slate-500">
              {"Three of many \u00b7 A4 portrait \u00b7 print-ready"}
            </div>
          </div>
        </motion.div>

        {/* Copy */}
        {/* No col-span below lg: the grid is `grid-cols-1` there, so a
            12-column span creates 11 implicit auto columns and squeezes the
            certificate column to roughly half width (tiny thumbnail with dead
            space beside it) at every 640–1023 px viewport. */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="eyebrow">Verifiable evidence</div>
          <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
            Competency, across every{" "}
            <span className="text-[#D4A65A]">compliance area</span>.
          </h2>
          <p className="mt-5 text-slate-400 text-lg leading-relaxed max-w-md">
            Every acknowledgement is signed, time stamped and issued as a
            named competency record. From Controlled Drug Handling to GDP
            Compliance, every regulated area is proven on paper.
          </p>

          <ul className="mt-8 space-y-2.5 max-w-md" data-testid="certificate-competency-list">
            {[
              "Controlled Drug Handling",
              "Medical Cannabis Dispensing",
              "GDP Compliance",
              "SOP Competency Verification",
              "Patient Record Management",
            ].map((c) => (
              <li
                key={c}
                className="flex items-center gap-3 text-[13.5px] text-slate-300"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A] shrink-0" />
                <span>Certificate of Competency — {c}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Retention
              </div>
              <div className="mt-1 font-display text-2xl text-white tracking-tight">
                7 years
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Tamper evident
              </div>
              <div className="mt-1 font-display text-2xl text-white tracking-tight">
                SHA 256
              </div>
            </div>
          </div>

          <p className="mt-6 text-[11px] text-slate-500 uppercase tracking-[0.18em]">
            Issued by <BrandName />
          </p>
        </div>
      </div>
    </section>
  );
}
